import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { Provider, useSelector } from "react-redux";
import store from "./redux/store";
import Landing from "./pages/Landing";
import Navbar from "./layout/Navbar";
import Mui from "./styles/Mui";
import NewsPage from "./pages/NewsPage";
import SavedPage from "./pages/SavedPage";
import LoginPage from "./pages/LoginPage";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch } from "react-redux";
import { loginUser, logoutUser } from "./redux/features/authSlice";
import SplashScreen from "./layout/SplashScreen";
import { authAPI, homeUrl } from "./apis";
import { setProfilePicUrl } from "./redux/features/dataSlice";
import NewsCategoryPage from "./pages/NewsCategoryPage";
import EmailConfirm from "./pages/EmailConfirm/EmailConfirm";
import EmailMessage from "./pages/EmailConfirm/EmailMessage";
import ChangePassword from "./pages/ChangePassword";
import ResetPassword from "./pages/ResetPassword";
import PopupFeedback from "./layout/popup/PopupFeedback";
import PopupCollection from "./layout/popup/PopupCollection";
import AdminPage from "./pages/AdminPage";
import CollectionConfirmation from "./pages/SavedPage/popup/CollectionConfirmation";
import BooksPage from "./pages/BooksPage";
import WikipediaPage from "./pages/WikipediaPage";
import Settings from "./pages/SettingsPage";
import OauthLogin from "./pages/LoginPage/MicrosoftLogin";
import CleverLoggedIn from "./pages/LoginPage/CleverLoggedIn";
import ClassLinkLogin from "./pages/LoginPage/ClasslinkLogin";
import loggedInAxios, {
  axiosInstance,
  setupInterceptorsTo,
} from "./functions/axios";

function App() {
  const messageHandlers = (event) => {
    if (event.data.action === "setSessionStorage") {
      const { key, value } = event.data.payload;
      sessionStorage.setItem(key, value);
      console.log("Session storage set:", key, value);
      window.location.reload();
    }
    if (String(window.location.href) === `${homeUrl()}admin`) {
      sessionStorage.removeItem("user");
    }
  };

  useEffect(() => {
    window.addEventListener("message", messageHandlers);
    return () => {
      window.removeEventListener("message", messageHandlers);
    };
  }, []);

  return (
    <Provider store={store}>
      {/* <MsalProvider instance={msalInstance}> */}
      <BrowserRouter>
        <Mui>
          <Router />
        </Mui>
      </BrowserRouter>
      {/* </MsalProvider> */}
    </Provider>
  );
}

function Router() {
  const dispatch = useDispatch();

  const { darkTheme, collectionPopup } = useSelector((state) => state.styles);
  const { isAuthenticated, userLoading } = useSelector((state) => state.auth);
  const currentUser = useSelector((state) => state.auth.user);

  const updateReduxState = (newUser, invalid) => {
    if (invalid) {
      dispatch(loginUser({ user: newUser }));
    } else {
      let userFromStorage = sessionStorage.getItem("user")
        ? JSON.parse(sessionStorage.getItem("user"))
        : JSON.parse(localStorage.getItem("user"));

      if (
        !currentUser ||
        (userFromStorage && currentUser.userId !== userFromStorage.userId)
      ) {
        dispatch(loginUser({ user: userFromStorage }));
      }
    }
  };

  const logoutUserFromApp = () => {
    dispatch(logoutUser());
  };

  const checkUserFromStorage = () => {
    let user = {};
    if (sessionStorage.getItem("user")) {
      user = JSON.parse(sessionStorage.getItem("user"));
    } else {
      user = JSON.parse(localStorage.getItem("user"));
    }
    return user;
  };

  const getAvatar = async () => {
    const user = checkUserFromStorage();
    try {
      const { data } = await axiosInstance.get(
        `${authAPI.getUserDetails}?userId=${user?.userId}`
      );
      dispatch(setProfilePicUrl(data.profilePictureUrl));
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    setupInterceptorsTo(updateReduxState, logoutUserFromApp);
  }, []);

  useEffect(() => {
    getAvatar();
  }, [isAuthenticated]);

  if (userLoading) {
    return <SplashScreen />;
  }

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={darkTheme ? "dark" : "light"}
      />
      <PopupFeedback />
      {collectionPopup && <PopupCollection />}
      {!isAuthenticated ? (
        <>
          <Routes>
            {/* <Route
              path="/VerifySharedCollection"
              element={<CollectionConfirmation />}
            /> */}
            <Route index element={<Navigate replace to="/signin" />} />
            <Route path="/signin" element={<LoginPage />} />
            <Route path="/signin/microsoft" element={<OauthLogin />} />
            <Route path="/signin/clever" element={<OauthLogin />} />
            <Route path="/signin/classlink" element={<ClassLinkLogin />} />

            <Route path="/signup" element={<LoginPage />} />
            <Route path="/forgot-password" element={<LoginPage />} />
            <Route
              path="/SignUpMessage"
              element={<EmailMessage type="signup" />}
            />

            <Route
              path="/ResetPasswordMessage"
              element={<EmailMessage type="resetpassword" />}
            />
            <Route path="/ConfirmEmail" element={<EmailConfirm />} />
            <Route path="/ResetPassword" element={<ResetPassword />} />

            <Route path="*" element={<Navigate replace to="/" />} />
            <Route />
          </Routes>
        </>
      ) : (
        <>
          <Navbar />

          <Routes>
            <Route
              path="/VerifySharedCollection"
              element={<CollectionConfirmation />}
            />
            <Route index element={<Landing />} />
            <Route path="/search/:query" element={<NewsPage />} />

            <Route
              path="/category/:category/:query"
              element={<NewsCategoryPage />}
            />
            <Route path="/saved/:id" element={<SavedPage />} />
            <Route path="/change-password" element={<ChangePassword />} />

            <Route path="/admin" element={<AdminPage />} />
            <Route path="/books/:query" element={<BooksPage />} />
            <Route path="/wiki/:query" element={<WikipediaPage />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/signin/clever" element={<CleverLoggedIn />} />

            <Route path="*" element={<Navigate replace to="/" />} />
          </Routes>
        </>
      )}
    </>
  );
}

export default App;
